import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import api from '../api/axiosConfig';
import { BillableAnalysis, BillingState, CreateInvoicePayload } from '../types';
import { groupByDoctor } from '../utils/groupByDoctor';

interface Props {
    organizationId: number;
    show: boolean;
    onClose: () => void;
    onCreated: () => void;
}

const stateLabels: Record<BillingState, string> = {
    STARTED: 'Начат',
    ANALYZED: 'Проанализирован',
    DIAGNOSED: 'Диагноз получен',
    REPORTED: 'Отчёт сформирован',
    FAILED: 'Ошибка',
};

const stateVariants: Record<BillingState, string> = {
    STARTED: 'secondary',
    ANALYZED: 'info',
    DIAGNOSED: 'primary',
    REPORTED: 'success',
    FAILED: 'danger',
};

function defaultPeriod(): { from: string; to: string } {
    const now = new Date();
    const from = new Date(now);
    from.setDate(now.getDate() - 29);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { from: iso(from), to: iso(now) };
}

const CreateInvoiceModal: React.FC<Props> = ({ organizationId, show, onClose, onCreated }) => {
    const [period, setPeriod] = useState(defaultPeriod());
    const [analyses, setAnalyses] = useState<BillableAnalysis[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [prices, setPrices] = useState<Record<string, string>>({});
    const [defaultPrice, setDefaultPrice] = useState<string>('');
    const [notes, setNotes] = useState('');
    const [currency, setCurrency] = useState('KZT');
    const [saving, setSaving] = useState(false);

    const loadPreview = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get<BillableAnalysis[]>('/invoices/preview', {
                params: { organizationId, from: period.from, to: period.to },
            });
            setAnalyses(res.data);
            const initSelected: Record<string, boolean> = {};
            const initPrices: Record<string, string> = {};
            for (const a of res.data) {
                initSelected[a.analysisId] = true;
                initPrices[a.analysisId] = '';
            }
            setSelected(initSelected);
            setPrices(initPrices);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Не удалось загрузить анализы');
        } finally {
            setLoading(false);
        }
    }, [organizationId, period.from, period.to]);

    useEffect(() => {
        if (show) loadPreview();
    }, [show, loadPreview]);

    const applyDefaultPrice = () => {
        if (!defaultPrice) return;
        const next: Record<string, string> = { ...prices };
        for (const a of analyses) {
            if (selected[a.analysisId] && !next[a.analysisId]) {
                next[a.analysisId] = defaultPrice;
            }
        }
        setPrices(next);
    };

    const total = useMemo(() => {
        let sum = 0;
        for (const a of analyses) {
            if (!selected[a.analysisId]) continue;
            const p = parseFloat(prices[a.analysisId] || '0');
            if (!isNaN(p)) sum += p;
        }
        return sum;
    }, [analyses, selected, prices]);

    const selectedCount = useMemo(
        () => analyses.filter((a) => selected[a.analysisId]).length,
        [analyses, selected]
    );

    const groups = useMemo(() => groupByDoctor(analyses), [analyses]);

    const subtotalFor = (items: BillableAnalysis[]) => {
        let s = 0;
        for (const a of items) {
            if (!selected[a.analysisId]) continue;
            const p = parseFloat(prices[a.analysisId] || '0');
            if (!isNaN(p)) s += p;
        }
        return s;
    };

    const toggleGroup = (items: BillableAnalysis[], checked: boolean) => {
        const next = { ...selected };
        for (const a of items) next[a.analysisId] = checked;
        setSelected(next);
    };

    const submit = async (mode: 'withPrices' | 'withoutPrices') => {
        setError(null);
        const items: CreateInvoicePayload['items'] = [];
        for (const a of analyses) {
            if (!selected[a.analysisId]) continue;
            if (mode === 'withoutPrices') {
                items.push({ analysisId: a.analysisId });
                continue;
            }
            const raw = prices[a.analysisId];
            if (raw === undefined || raw === '') {
                items.push({ analysisId: a.analysisId });
                continue;
            }
            const p = parseFloat(raw);
            if (isNaN(p) || p < 0) {
                setError(`Некорректная цена для ${a.reportNumber ?? a.analysisId}`);
                return;
            }
            items.push({ analysisId: a.analysisId, unitPrice: p });
        }
        if (items.length === 0) {
            setError('Выберите хотя бы одну позицию');
            return;
        }

        const payload: CreateInvoicePayload = {
            organizationId,
            periodStart: period.from,
            periodEnd: period.to,
            currency,
            notes: notes || undefined,
            items,
        };
        setSaving(true);
        try {
            await api.post('/invoices', payload);
            onCreated();
        } catch (e: any) {
            setError(e.response?.data?.message || 'Не удалось создать счёт');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="xl" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Создание счёта</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3 mb-3 align-items-end">
                    <Col md={3}>
                        <Form.Label className="small text-muted">Период с</Form.Label>
                        <Form.Control type="date" value={period.from}
                                      onChange={(e) => setPeriod({ ...period, from: e.target.value })} />
                    </Col>
                    <Col md={3}>
                        <Form.Label className="small text-muted">Период по</Form.Label>
                        <Form.Control type="date" value={period.to}
                                      onChange={(e) => setPeriod({ ...period, to: e.target.value })} />
                    </Col>
                    <Col md="auto">
                        <Button variant="outline-secondary" size="sm"
                                onClick={() => setPeriod(defaultPeriod())}>
                            Последние 30 дней
                        </Button>
                    </Col>
                    <Col md={3}>
                        <Form.Label className="small text-muted">Цена по умолчанию</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control type="number" min="0" step="100" value={defaultPrice}
                                          onChange={(e) => setDefaultPrice(e.target.value)} />
                            <Button variant="outline-secondary" onClick={applyDefaultPrice}>Применить</Button>
                        </div>
                    </Col>
                    <Col md={3}>
                        <Form.Label className="small text-muted">Валюта</Form.Label>
                        <Form.Control value={currency} onChange={(e) => setCurrency(e.target.value)} />
                    </Col>
                </Row>

                {error && <Alert variant="danger">{error}</Alert>}

                {loading ? (
                    <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
                ) : (
                    <Table hover responsive size="sm">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: 32 }}></th>
                                <th>Дата запуска анализа</th>
                                <th>Номер отчёта</th>
                                <th>Состояние</th>
                                <th style={{ width: 140 }}>Цена</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map((g) => {
                                const allSelected = g.items.every((it) => selected[it.analysisId]);
                                const groupSubtotal = subtotalFor(g.items);
                                return (
                                    <React.Fragment key={g.doctorId ?? 'unknown'}>
                                        <tr className="table-secondary">
                                            <td>
                                                <Form.Check
                                                    checked={allSelected}
                                                    onChange={(e) => toggleGroup(g.items, e.target.checked)}
                                                />
                                            </td>
                                            <td colSpan={3} className="fw-semibold">
                                                Врач: {g.doctorName} <span className="text-muted fw-normal">({g.items.length})</span>
                                            </td>
                                            <td className="text-end text-muted small">
                                                {groupSubtotal.toFixed(2)} {currency}
                                            </td>
                                        </tr>
                                        {g.items.map((a) => (
                                            <tr key={a.analysisId}>
                                                <td>
                                                    <Form.Check
                                                        checked={!!selected[a.analysisId]}
                                                        onChange={(e) => setSelected({ ...selected, [a.analysisId]: e.target.checked })}
                                                    />
                                                </td>
                                                <td>{a.startedAt?.replace('T', ' ').slice(0, 16) ?? '—'}</td>
                                                <td><code>{a.reportNumber ?? '—'}</code></td>
                                                <td><Badge bg={stateVariants[a.billingState]}>{stateLabels[a.billingState]}</Badge></td>
                                                <td>
                                                    <Form.Control type="number" min="0" step="100" size="sm"
                                                                  value={prices[a.analysisId] ?? ''}
                                                                  disabled={!selected[a.analysisId]}
                                                                  onChange={(e) => setPrices({ ...prices, [a.analysisId]: e.target.value })} />
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                            {analyses.length === 0 && (
                                <tr><td colSpan={5} className="text-center text-muted py-4">
                                    Нет невыставленных анализов в этом периоде
                                </td></tr>
                            )}
                        </tbody>
                    </Table>
                )}

                <Form.Group className="mt-3">
                    <Form.Label className="small text-muted">Примечания</Form.Label>
                    <Form.Control as="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </Form.Group>

                <div className="d-flex justify-content-end mt-3">
                    <div className="text-end">
                        <div className="text-muted small">Позиций выбрано: {selectedCount}</div>
                        <div className="text-muted small">Цены можно оставить пустыми — бухгалтерия добавит позже.</div>
                        <div className="fs-5"><strong>Промежуточный итог: {total.toFixed(2)} {currency}</strong></div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Отмена</Button>
                <Button variant="outline-primary" onClick={() => submit('withoutPrices')}
                        disabled={saving || selectedCount === 0}>
                    Создать без цен
                </Button>
                <Button variant="primary" onClick={() => submit('withPrices')}
                        disabled={saving || selectedCount === 0}>
                    {saving ? <Spinner size="sm" animation="border" /> : 'Создать счёт'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateInvoiceModal;
